<script lang="ts" setup>
import {computed, onMounted, reactive, ref, watch } from 'vue';
import { ChoiceApi, DropDownSplitButton, DropDownButtonItem, SectionPanel, TitledRow, PageTitle } from '../../LibMain';
import { ISentinelApiKey, ISentinelNewKey , IUpdateSentinelKeyRequest, SentinelApplication, ICompanies, ISentinelRoute, ISentinelRouteMap, IWebserviceAccounts} from '../../lib/Api/Sentinel';
import _ from 'lodash';
import { useSentinelStore } from '../../lib/Stores/SentinelStore';
import { initPinia } from '../../lib/PiniaMgr';
import TextInput from "../TextInput.vue";
import { ICol, RenderModeType } from '../../lib/ChoiceTableHelpers';
import ChoiceTable from "../ChoiceTable.vue"
import { ValidationResults } from '../../lib/Models/ValidationResults';
import * as Validation from "../../lib/Validation";
import { useRouter } from 'vue-router';


const props = withDefaults(defineProps<{
  id?:number,
  placeholder?:string
}>(), {
  placeholder:"Search Database Name"
});

interface IBaseKeyDetails{
  selectedKey: ISentinelApiKey | undefined
  companyList: ICompanies[] | undefined
  sentinelRoute: ISentinelRoute[] | undefined
  webAccounts: IWebserviceAccounts[] | undefined
  routeMap: ISentinelRouteMap[] | undefined
}
const base = reactive<IBaseKeyDetails>({
  selectedKey:undefined,
  companyList:undefined,
  sentinelRoute:undefined,
  webAccounts:undefined,
  routeMap:undefined
});

const compData = reactive<{
  webservice?:string,
  KeyDescriptionInput?:string,
  RateLimitPerHourInput?:number,
  DatabaseKey?:string,
  DatabaseName?:string,
  isActive?:boolean,
  SentinelApplication?:SentinelApplication
}>({
});

const createdDate = ref<Date>();

const tableColumns = ref<ICol[]>([
  { Name: 'Set Route to Key', DataField:"selected", RenderMode: RenderModeType.Selection },
  { Name: 'Route Id', DataField: 'sentinelRouteId', HideFilter: true},
  { Name: 'Route Name', DataField: 'routeName'},
  { Name: 'Route Description', DataField: 'routeDescription'},
  { Name: 'Section Name', DataField: 'sectionName'}
]);




const IsActiveOptions = [
  {opt:true,name:"True"},
  {opt:false,name:"False"}];

const currentIsActiveName = computed<string>(() =>
  IsActiveOptions.find(o => o.opt === compData.isActive )?.name ?? "Select Active Status"
);


const sentinelStore = useSentinelStore(initPinia());

const windowHeightStyle = ref<string>();

onMounted(async() =>{
  windowHeightStyle.value = "height: " + (window.innerHeight)+"px";
  window.addEventListener('resize', () => {
      windowHeightStyle.value = "height: " + (window.innerHeight )+"px";
  });
  //This is defaulted to Choice always
  compData.SentinelApplication = SentinelApplication.Choice;
  //Gets the route map from the local store if it exists. If not, retrieves it from the server
  base.sentinelRoute = await sentinelStore.getSentinelRoute();
  if(!base.sentinelRoute){
    return;
  }
  base.routeMap = await sentinelStore.getSentinelRouteMap();
  if(!base.routeMap){
    return;
  }
  base.sentinelRoute = base.sentinelRoute.map(o =>{
     return { ...o, selected: isSelected(o)}});
  if(!props.id){

    compData.isActive = true;

    base.companyList = await sentinelStore.getAllCompanies();
    if(!base.companyList){
      return;
    }
  
    base.webAccounts = await sentinelStore.getAllWebserviceAccounts()
    if(!base.webAccounts){
      return;
    }
    return;
  }
  base.selectedKey = await sentinelStore.getKey(props.id);
  if(!base.selectedKey){
    return;
  }
  //After retreiving the key details through selected key, we set it to a local reactive compData for editing and viewing
  compData.webservice = base.selectedKey.webserviceAccount;
  compData.DatabaseKey = base.selectedKey.databaseKey;
  compData.DatabaseName = base.selectedKey.databaseName;
  compData.RateLimitPerHourInput = base.selectedKey.rateLimitPerHour;
  compData.KeyDescriptionInput = base.selectedKey.sentinelKeyDescription;
  compData.isActive = base.selectedKey.isActive;
  createdDate.value = new Date(base.selectedKey.createdDateTimeUTC.replace('T', ' ') + 'Z');
});

const router = useRouter();

//Company Search Section in <script>

const searchCompaniesTerm = ref();

const showCompaniesList = ref<boolean>(false);

const companiesFilteredList = computed(() => {
  if (!base.companyList) {
    return;
  }
  return base.companyList.filter(
    item => item.database_name.toLowerCase().includes(searchCompaniesTerm.value.toLowerCase())
  );
});

watch(searchCompaniesTerm, () => {
    if(!searchCompaniesTerm.value){
      showCompaniesList.value = false;
    }
    else{
      showCompaniesList.value = true;
    }
  });

  function isSelected(route:ISentinelRoute):boolean{
    if(!base.routeMap){
      return false;
    }
    if(!base.sentinelRoute){
      return false;
    }
    return base.routeMap.some(o => (o.sentinelRouteId === route.sentinelRouteId && o.sentinelApiKeyId === props.id));
    
  }

  function onClickSelectCompany(item: ICompanies){
     // Empty the search term
    searchCompaniesTerm.value = undefined;
    //Sets the properties from the selected company to the local sentinel key
    compData.DatabaseName = item.database_name;
    compData.DatabaseKey = item.database_key;
    //This has to be set to false to hide the list after selection
    showCompaniesList.value = false;
  };


//Webservice Account Search Section in <script>

const searchWebAccountsTerm = ref();

const showWebAccountsList = ref<boolean>(false);

const webserviceAccountsFilteredList = computed(() => {
  if (!base.webAccounts) {
    return;
  }
  return base.webAccounts.filter(
    item => item.description.toLowerCase().includes(searchWebAccountsTerm.value.toLowerCase())
  );
});

watch(searchWebAccountsTerm, () => {
  if(!searchWebAccountsTerm.value){
    showWebAccountsList.value = false;
  }
  else{
    showWebAccountsList.value = true;
  }
});

function onClickSelectWebserviceAccount(item: IWebserviceAccounts){
   // Empty the search term
   searchWebAccountsTerm.value = undefined;
  //Sets the properties from the selected webservice account to the local sentinel key
  compData.webservice = item.account;
  //This has to be set to false to hide the list after selection
  showWebAccountsList.value = false;
};




async function saveAll(){
  if(props.id){
    await updateKey();
    router.push({name:"Keys", path:"/"});
    return;
  }
  await generateKeyByInput();
  await sentinelStore.refreshKeysAndMap();
  router.push({name:"Keys", path:"/"});
}
async function saveRouteMap(){
  if(!base.sentinelRoute){
    return;
  }
  if(!props.id){
    return;
  }
  //Filters out all sentinelMap objects from the list if it is in the ID, deleting old mappings effectively to avoid having duplicates, plus easier handling
  base.routeMap = base.routeMap?.filter(o=>o.sentinelApiKeyId !== props.id);
  //Checks to see if a route was selected in the UI. If it was, it will be included in this list
  const selectedRouteIds = base.sentinelRoute.filter(o => o.selected===true);
  //Assigns mapping from the selected routes to the ApiKey and adds them all to the list of mappings
  base.routeMap = base.routeMap?.concat(selectedRouteIds.map(o=><ISentinelRouteMap>{sentinelApiKeyId:props.id, sentinelRouteId:o.sentinelRouteId}));
  //stores it locally
  const retval = <number[]>(selectedRouteIds.map(o => o.sentinelRouteId ));
  await ChoiceApi.sentinel.updateMap(props.id, retval)
  sentinelStore.storeMap(base.routeMap);
}

function generateRouteList(){
  if(!base.sentinelRoute){
    return;
  }
  const selectedRouteIds = base.sentinelRoute.filter(o => o.selected===true);
  const retval = <number[]>(selectedRouteIds.map(o => o.sentinelRouteId ));
  return retval;

}

function PositiveNonZeroNumber_WithMessage(value: number): ValidationResults
    {
        if (value >= 0 && value <= 10000)
          return {isValid: true};
          
        return { isValid: false, errorMsg: "Must be below 10000"};
    }

//This function sends the information to the server to update the Key, while calling updateKeyToLocalStore() to up date the key locally aswell
async function updateKey(){
  await saveRouteMap();
  if(!base.selectedKey){
    return;
  }
  //Remakes the iSentinelApiKey object with UI changes from compData and stores it locally
  const localretval = <ISentinelApiKey>({
    sentinelApiKeyID: base.selectedKey.sentinelApiKeyID,
    sentinelApiKey: base.selectedKey.sentinelApiKey,
    webserviceAccount: base.selectedKey.webserviceAccount,
    sentinelKeyDescription: compData.KeyDescriptionInput,
    databaseKey: base.selectedKey.databaseKey,
    databaseName: base.selectedKey.databaseName,
    rateLimitPerHour: compData.RateLimitPerHourInput,
    sentinelApplication: compData.SentinelApplication,
    createdDateTimeUTC: base.selectedKey.createdDateTimeUTC,
    isActive: compData.isActive
  });
  sentinelStore.updateKey(localretval);
  const retval = <IUpdateSentinelKeyRequest>({
    sentinelKeyDescription: compData.KeyDescriptionInput,
    rateLimitPerHour: compData.RateLimitPerHourInput,
    isActive: compData.isActive
  });
  return await ChoiceApi.sentinel.updateKey(base.selectedKey.sentinelApiKeyID, retval);
}

//This sends the info to the server so it can generate the new key
async function generateKeyByInput()
{
  const routeList = generateRouteList();
  const retVal = <ISentinelNewKey>({
    webserviceAccount: compData.webservice,
    sentinelKeyDescription: compData.KeyDescriptionInput,
    databaseKey: compData.DatabaseKey,
    rateLimitPerHour: compData.RateLimitPerHourInput,
    sentinelRouteIds: routeList
  });
  return await ChoiceApi.sentinel.generateKey(retVal);
}

function updateIsActive(opt:boolean){
  compData.isActive = opt;
}


const title = computed<string>( () => {
  if(!props.id){
    return "Generate New Sentinel Key";
  }
  return "Sentinel API Key Details";
});

const invalidSave = computed<boolean>(() => {
  if(!compData.RateLimitPerHourInput || compData.RateLimitPerHourInput > 10000){
    return false;
  }
  if(!props.id && (!compData.webservice || compData.webservice === "")){
    return false;
  }
  if(!props.id && (!compData.DatabaseKey || compData.DatabaseKey === "")){
    return false;
  }
  return true;
});
</script>
  <template>
    <div :style="windowHeightStyle" class="scrollable" >
      <PageTitle>
        <template #title>
          {{ title }}
        </template>
      </PageTitle>
      <button class="btn btn-primary btn-sm btn-key" title="GenerateNewKey" @click="saveAll()" :disabled="!invalidSave">
        Save
      </button>
      <RouterLink to="/">
        <button class="btn btn-primary btn-sm btn-key">
          Cancel
        </button>
      </RouterLink>
      <SectionPanel :title="'Edit Details'">
        <div v-if="!props.id">
          <TitledRow title="Search Database Name" :titleWidth="2">
            <TextInput v-model:textValue="searchCompaniesTerm" type="text" placeholder="Search" />
            <ul class="dropdown" v-if="showCompaniesList">
              <li v-for="item in companiesFilteredList" :key="item.database_name" class="dropdown-item" @click="onClickSelectCompany(item)">
                <div class="item-id">{{ item.database_name }}</div>
                <div class="item-name">{{ item.database_alias }}</div>
              </li>
            </ul>
          </TitledRow>
        </div>
        <div v-if="props.id">
          <TitledRow title="Sentinel ID" :titleWidth="2">
            {{ base.selectedKey?.sentinelApiKeyID }}
          </TitledRow>
          <TitledRow title="Sentinel API Key" :titleWidth="2">
            {{base.selectedKey?.sentinelApiKey}}
          </TitledRow>
        </div>
        <TitledRow title="Database Key" :titleWidth="2">      
          {{ compData.DatabaseKey }}
        </TitledRow>
        <TitledRow title="Database Name" :titleWidth="2">
          {{ compData.DatabaseName }}
        </TitledRow>
        <div v-if="!props.id">
        <TitledRow title="Search Webservice Accounts" :titleWidth="2">
            <TextInput v-model:textValue="searchWebAccountsTerm" type="text" placeholder="Search" />
            <ul class="dropdown" v-if="showWebAccountsList">
              <li v-for="item in webserviceAccountsFilteredList" :key="item.description" class="dropdown-item" @click="onClickSelectWebserviceAccount(item)">
                <div class="item-id">{{ item.description }}</div>
                <div class="item-name">{{ item.account }}</div>
              </li>
            </ul>
          </TitledRow>
        </div>
          <TitledRow title="Web Service Account" :titleWidth="2">
            {{ compData.webservice }}
          </TitledRow>
        <TitledRow title="Key Description" :titleWidth="2">
          <TextInput v-model:textValue="compData.KeyDescriptionInput" placeholder="Key Description" :maxLength="100"/>
        </TitledRow>
        <TitledRow title="Rate Limit per Hour" :titleWidth="2">
          <TextInput v-model:textValue="compData.RateLimitPerHourInput" placeholder="RateLimitPerHour" :validator="PositiveNonZeroNumber_WithMessage" :acceptCharacter="Validation.LimitToNumberCharacters"/>
        </TitledRow>
        <TitledRow title="Is Active" :titleWidth="2">
          <div v-if="props.id">
            <DropDownSplitButton>
              <template v-slot:button>{{ currentIsActiveName }}</template>
              <DropDownButtonItem v-for="activeOption in IsActiveOptions" :data="activeOption.opt" :width="{amount: 70}" @click="updateIsActive">
                {{ activeOption.name }}
              </DropDownButtonItem>
            </DropDownSplitButton>
          </div>
          <div v-if="!props.id">
            True
          </div>
          </TitledRow>
          <TitledRow title="Sentinel Application" :titleWidth="2">
              Choice
        </TitledRow>
        <div v-if="props.id">
          <TitledRow title="Created Date" :titleWidth="2">
            {{ createdDate }}
          </TitledRow>
        </div>
      </SectionPanel>

      <ChoiceTable
      :data="base.sentinelRoute"
      :columns="tableColumns"
      :width="1340"
      :show-filters="true"
      :allowSorting="true"
      :checkBoxScale="1.2"
      :key-value-data-member="'API Key ID'"
      />
    </div>
  </template>
<style scoped>
.dropdown {
  position: absolute;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
  background-color: #ccc;
  z-index: 2;
}

.dropdown-item {
  
  display: flex;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid #ccc;
  cursor: pointer;
  background-color: #ccc;
}

.item-id {
  font-weight: bold;
  margin-right: 8px;
}

.item-name {
  flex: 1;
}
.btn-key {
  margin-bottom: 10px;
  margin-right: 5px;
}
.scrollable {
   overflow-y: auto;
}
</style>